/**
 * Native (autonym) display names for the language codes shown in the app's
 * language picker (see Header.js), keyed by the code the Focus ontology API
 * returns from GET /api/ontology/all/languages.
 *
 * This is deliberately a small hardcoded map, not something read from
 * PoolParty: that endpoint enumerates which language codes have concept
 * label translations in the ontology (i.e. which languages PoolParty has
 * content for), it does not - and conceptually cannot - supply "what is
 * language X called in language X itself". That's a static linguistic
 * fact, not taxonomy content, so there is nothing to fetch it from. Single
 * shared location so every place that needs a friendly language name reads
 * from here rather than each keeping its own copy.
 */
const LANGUAGE_NATIVE_NAMES = {
  en: 'English',
  nl: 'Nederlands',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  pt: 'Português',
  it: 'Italiano',
  sw: 'Kiswahili',
  hi: 'हिन्दी',
  id: 'Bahasa Indonesia',
  zh: '中文',
  ar: 'العربية',
  ru: 'Русский',
  ja: '日本語',
  ko: '한국어',
  th: 'ภาษาไทย',
  vi: 'Tiếng Việt',
  tr: 'Türkçe',
  pl: 'Polski',
  am: 'አማርኛ',
  bg: 'Български',
  ro: 'Română',
  sk: 'Slovenčina',
  sl: 'Slovenščina',
  mg: 'Malagasy',
  gl: 'Galego',
  uk: 'Українська'
};

/**
 * Returns the native display name for a language code, falling back to the
 * raw code itself if it isn't in the map - never hide a language the
 * ontology actually offers just because this map hasn't been extended for
 * it yet.
 *
 * Some codes come back as a language + region pair (e.g. 'sk-sk', 'sl-si',
 * 'gl-es' - Focus/PoolParty language tags aren't always a plain ISO 639-1
 * code) rather than a duplicate top-level entry for the same language.
 * Falling back to the primary subtag (the part before the '-') covers those
 * without hardcoding every region variant separately.
 * @param {string} code - e.g. 'en', 'nl', 'sk-sk'.
 * @returns {string}
 */
const getLanguageDisplayName = code => {
  if (!code) return code;
  if (LANGUAGE_NATIVE_NAMES[code]) return LANGUAGE_NATIVE_NAMES[code];
  const primarySubtag = code.split('-')[0];
  return LANGUAGE_NATIVE_NAMES[primarySubtag] || code;
};

/**
 * The ontology's language list can contain more than one code that resolves
 * to the same display name (e.g. 'sk' and 'sk-sk' both -> Slovenčina, 'sl'
 * and 'sl-si' both -> Slovenščina) - a plain code plus a redundant
 * language+region variant for the same language, rather than two distinct
 * languages. Keeps only the shortest code per display name (the plain
 * 2-letter one, when present) so the picker doesn't show the same language
 * twice; a code with no shorter counterpart (e.g. 'gl-es', with no plain
 * 'gl' in the list) is left as the only entry for its language.
 * @param {string[]} codes
 * @returns {string[]} the input codes, filtered, in their original order.
 */
const dedupeLanguageCodes = codes => {
  if (!Array.isArray(codes)) return [];
  const shortestCodeByName = new Map();
  codes.forEach(code => {
    const name = getLanguageDisplayName(code);
    const current = shortestCodeByName.get(name);
    if (!current || code.length < current.length) {
      shortestCodeByName.set(name, code);
    }
  });
  const winningCodes = new Set(shortestCodeByName.values());
  return codes.filter(code => winningCodes.has(code));
};

export { getLanguageDisplayName, dedupeLanguageCodes };
